import axios from 'axios'
import store from '@/store'
import { Events } from '@/events'
import router from '@/router'

const RETRY_MAXCOUNT = 3
const RETRY_DELAY = 100

const axiosAPI = axios.create({
  baseURL: window.globals.VUE_APP_APIBASE
})

const showInvalidTokenModal = () => {
  Events.$emit('modalOpen', {
    type: 'error',
    icon: 'nodoc.png',
    title: 'Authentication could not be refreshed',
    body: 'The authentication with the server has expired and could not be refreshed. Please log in again to continue using the application.',
    actions: [
      {
        label: 'Log in',
        class: 'input-button-primary',
        action () {
          // redirect to SSO login page
          const route = router.currentRoute
          let SSO_URL = `${store.state.auth.SSO.url}/application/${store.state.auth.SSO.appId}`

          if (route.name === 'publicationloader' && route.params.pubKey) SSO_URL += '&pubKey=' + route.params.pubKey
          if (route.name === 'reader' && route.params.docKey) SSO_URL += '&docKey=' + route.params.docKey

          window.location = SSO_URL
        }
      }
    ],
    requireAction: true
  })
}

const showGeneralErrorModal = (originalRequest) => {
  return new Promise((resolve, reject) => {
    Events.$emit('modalOpen', {
      type: 'error',
      icon: 'server.png',
      title: 'Server could not be reached',
      body: 'There was a problem establishing a connection to the server. Please try again.',
      actions: [
        {
          label: 'Try again',
          class: 'input-button-primary',
          action () {
            Events.$emit('modalClose')

            // send try the same request again
            resolve(axiosAPI.request(originalRequest))
          }
        }
      ],
      requireAction: true
    })
  })
}

const retryRequest = (error, originalRequest) => {
  return new Promise((resolve, reject) => {
    let retryCount = originalRequest.retryCount
    if (retryCount >= RETRY_MAXCOUNT) {
      reject(error)
    } else {
      retryCount++
      originalRequest.retryCount = retryCount
      setTimeout(() => {
        resolve(axiosAPI.request(originalRequest))
      }, retryCount * RETRY_DELAY)
    }
  })
}

const refreshToken = () => {
  // When the store is already refreshing return the promise so the interceptor can wait until the refresh is done before sending the call again
  if (store.state.auth.refreshing) {
    return store.state.auth.currentRefresh
  }

  // The store creates a refresh call and returns the promise, so other API calls that come in 401 during refreshing can listen to it
  // and react to it when refreshing is done (send the requests again)
  return store.dispatch('auth/refreshTokens')
}

axiosAPI.interceptors.request.use(
  config => {
    const auth = store.getters['auth/getTokens']

    if (auth.accessToken) {
      config.headers.Authorization = 'Bearer ' + auth.accessToken
    }

    if (typeof config.retryCount === 'undefined') {
      config.retryCount = 0
    }

    return config
  },
  error => Promise.reject(error)
)

axiosAPI.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config

    if (!error.response) {
      return showGeneralErrorModal(originalRequest)
    }

    if (error.response.status === 502 || error.response.status === 503) {
      return retryRequest(error, originalRequest)
    }

    if (error.response.status === 401) {
      const refreshed = await refreshToken()
      if (!refreshed) {
        return showInvalidTokenModal()
      }

      return axiosAPI.request(originalRequest)
    }

    return Promise.reject(error)
  }
)

export default axiosAPI
